/**
 * MER-692: calendar-month selection for the global Range picker.
 *
 * WHY. The picker offered only MTD / WTD / L30 / L90 / YTD, so no setting on
 * the dashboard isolated a single calendar month. A client comparing a tile
 * against their own month-end workbook had no way to line the two up: monthly
 * tiles defaulted to the last complete month, weekly and event-grain tiles to
 * a trailing 30 days, and the two windows were different lengths ending on
 * different days. The same month agreed; the default view did not.
 *
 * THE TOKEN. `?range=YYYY-MM` is an explicit range in the same URL slot as the
 * presets. It maps to the manifest range token `month:YYYY-MM`, which rides
 * through applyGlobalFilters and resolveGrainAndRange exactly the way
 * `this_month` or `last_30_days` does, and is resolved to bounds at the query
 * layer (queries.ts applyTimeline -> periodWindow.mjs for period-bucketed
 * tables, monthBoundsFor for everything else).
 *
 * A month is deterministic: the same token selects the same rows on any day.
 * That is what makes a shared link, or a comparison against a workbook, stable.
 *
 * Pure and dependency-free so node:test imports it directly (the nowCutoff.mjs
 * precedent); calendarMonth.d.mts types it for the TypeScript callers. All
 * calendar math runs on LOCAL date parts and never toISOString, so a UTC
 * offset cannot move a boundary by a day.
 */

/** The preset range values, in selector order. globalFilters.ts reads these. */
export const RANGE_PRESETS = Object.freeze(["mtd", "wtd", "l30", "l90", "ytd"]);

/** A bare calendar month key: four-digit year, two-digit month 01-12. */
export const MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Prefix of the manifest range token a month selection maps to. */
export const MONTH_TOKEN_PREFIX = "month:";

/**
 * MER-614: the three-letter month names the dashboard formats dates with.
 * Exported so tileWindow.mjs can name the last day a tile's window includes
 * without keeping a second copy of the list that could drift from the one
 * monthLabel() renders.
 */
export const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** True for a string shaped YYYY-MM with a real month. */
export function isMonthKey(value) {
  return typeof value === "string" && MONTH_KEY_RE.test(value);
}

/** 'YYYY-MM' -> 'month:YYYY-MM'; null for anything that is not a month key. */
export function monthRangeToken(month) {
  return isMonthKey(month) ? `${MONTH_TOKEN_PREFIX}${month}` : null;
}

/** 'month:YYYY-MM' -> 'YYYY-MM'; null for any other range (presets included). */
export function parseMonthToken(token) {
  if (typeof token !== "string" || !token.startsWith(MONTH_TOKEN_PREFIX)) return null;
  const month = token.slice(MONTH_TOKEN_PREFIX.length);
  return isMonthKey(month) ? month : null;
}

/**
 * Classify the raw `range` URL param.
 *
 *   a preset value   -> { kind: 'preset', range }
 *   a YYYY-MM month  -> { kind: 'month', month }
 *   anything else    -> null, which callers treat exactly as a missing param
 *                       (the pre-existing behaviour for an invalid value)
 */
export function parseRangeParam(raw) {
  if (typeof raw !== "string") return null;
  if (RANGE_PRESETS.includes(raw)) return { kind: "preset", range: raw };
  if (isMonthKey(raw)) return { kind: "month", month: raw };
  return null;
}

function parts(month) {
  return { y: Number(month.slice(0, 4)), m: Number(month.slice(5, 7)) };
}

function keyOf(y, monthIndex) {
  // monthIndex is zero-based and may overflow either way; Date normalizes it.
  const d = new Date(y, monthIndex, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** '2026-07' -> 'Jul 2026'; null for anything that is not a month key. */
export function monthLabel(month) {
  if (!isMonthKey(month)) return null;
  const { y, m } = parts(month);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

/** '2026-07' -> '2026-06'; '2026-01' -> '2025-12'; null when not a month key. */
export function previousMonth(month) {
  if (!isMonthKey(month)) return null;
  const { y, m } = parts(month);
  return keyOf(y, m - 2);
}

/** '2026-07' -> '2026-08'; '2026-12' -> '2027-01'; null when not a month key. */
export function nextMonth(month) {
  if (!isMonthKey(month)) return null;
  const { y, m } = parts(month);
  return keyOf(y, m);
}

/** The month key `today` falls in, from local calendar parts. */
export function monthKeyOf(today) {
  return keyOf(today.getFullYear(), today.getMonth());
}

/**
 * True when `month` ended before `today`'s month began: neither the month in
 * progress nor a future one. Month keys sort lexicographically in calendar
 * order, so this is a plain string comparison.
 */
export function isCompleteMonth(month, today) {
  return isMonthKey(month) && month < monthKeyOf(today);
}

/**
 * The `n` most recent COMPLETE calendar months before `today`, newest first.
 * The month in progress is never offered: a partial month read against a
 * complete month-end figure is the partial-vs-full comparison trap.
 */
export function recentCompleteMonths(today, n) {
  const count = Math.max(0, Math.trunc(Number(n) || 0));
  const out = [];
  for (let i = 1; i <= count; i += 1) {
    out.push(keyOf(today.getFullYear(), today.getMonth() - i));
  }
  return out;
}

/**
 * The Month select's label for `month`. A complete month reads plainly
 * ('Jul 2026'). The month in progress and a future month are never offered,
 * but either can arrive through a typed or old link, and then the select
 * still has to name the window the page is showing: 'Sep 2026 (in progress)'
 * or 'Oct 2026 (future)'. null when `month` is not a month key.
 */
export function monthOptionLabel(month, today) {
  const label = monthLabel(month);
  if (label === null) return null;
  const current = monthKeyOf(today);
  if (month === current) return `${label} (in progress)`;
  if (month > current) return `${label} (future)`;
  return label;
}

/**
 * The Month select's options, newest first: the `count` most recent complete
 * months, plus the active month when it is not among them (older than the
 * list, in progress, or future), so the control always names the active
 * window. Each option is `{ value: 'YYYY-MM', label }`.
 */
export function monthSelectOptions(today, count, activeMonth) {
  const offered = recentCompleteMonths(today, count);
  const values =
    isMonthKey(activeMonth) && !offered.includes(activeMonth)
      ? [...offered, activeMonth].sort().reverse()
      : offered;
  return values.map((value) => ({ value, label: monthOptionLabel(value, today) }));
}

/**
 * Inclusive-lower / exclusive-upper bounds for one calendar month, serialized
 * for the column's storage type.
 *
 * The schema convention (monthBounds in queries.ts states it): a column whose
 * name ends in `_date` is a real DATE and takes 'YYYY-MM-DD'; every other
 * period column is TEXT holding a bare 'YYYY-MM' key, where an ISO date would
 * sort after the month it belongs to and drop every row (MER-138).
 *
 * @returns {{ gte: string, lt: string } | null} null when `month` is not a month key.
 */
export function monthBoundsFor(month, column) {
  if (!isMonthKey(month)) return null;
  const next = nextMonth(month);
  if (String(column).endsWith("_date")) {
    return { gte: `${month}-01`, lt: `${next}-01` };
  }
  return { gte: month, lt: next };
}
